import { useState } from 'react'
import { supabase } from '../lib/supabase'

/**
 * The front door.
 *
 * A password, and nothing else. Sign-in is a hard gate, and a magic link makes
 * every entry depend on mail being delivered: a link stuck in a corporate
 * quarantine is indistinguishable, to its owner, from a broken app. A password
 * asks the server a question the server can answer by itself, which is what
 * takes email out of this product entirely.
 *
 * A link that signs somebody straight in was here and was taken out. Mail is
 * back for exactly one thing, which is the thing it is actually good at:
 * somebody who has forgotten the password cannot be asked for it, and there is
 * nowhere else to send them. That is a reset rather than a way in, so it lands
 * on a screen that makes them choose a new password instead of dropping them
 * into the app with the old one still set. See SetPassword.
 *
 * This does not create an account either. Signup is closed in the project,
 * because document reading runs on an inference key that is ours and a
 * per-account daily cap means nothing if accounts are free.
 *
 * Laid out like asvise.sg's own sign-in, which is the one screen somebody meets
 * before they have any other reason to believe the two are the same product.
 * The rest of the app already borrows the brand's tokens; this borrows its
 * shape, so arriving here from asvise.sg does not feel like leaving.
 */

/**
 * The one thing a failed sign-in is allowed to say.
 *
 * Not which half was wrong, because saying so tells anyone with a list of
 * addresses which of them have accounts here. It has to cover the closed-signup
 * refusal too: distinguishing "wrong password" from "no such account" is the
 * same disclosure by a different route, and now that signup is closed there is
 * an address-checking oracle sitting behind that distinction.
 */
const WRONG =
  'That email and password do not match an account. Accounts here are set up for you, so if you do not have one yet, ask us rather than signing up.'

/**
 * How the server declines to act on an address it does not know.
 *
 * Several wordings, because a closed signup is refused differently depending on
 * which endpoint asked and which release is answering.
 */
const NO_SUCH_ACCOUNT = /signups? not allowed|signup_disabled|user not found/i

/** What Supabase said, in words that tell the reader what to do next. */
function readable(message: string): string {
  if (/invalid login credentials/i.test(message) || NO_SUCH_ACCOUNT.test(message)) return WRONG
  return message
}

/**
 * The board, drawn as furniture rather than as data.
 *
 * Sits at a few percent opacity behind the brand panel, which is where asvise
 * puts a wireframe of the thing its product is about. Theirs is a list of
 * advisers; this is what this product actually shows you, which is a set of
 * exposure cards narrowing from possible to settled.
 */
function BoardWireframe() {
  return (
    <svg
      viewBox="0 0 300 260"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="auth-wireframe"
      aria-hidden="true"
    >
      <rect x="20" y="12" width="120" height="72" rx="12" stroke="white" strokeWidth="1.5" />
      <circle cx="40" cy="32" r="5" stroke="white" strokeWidth="1.5" />
      <rect x="54" y="28" width="62" height="7" rx="3.5" stroke="white" strokeWidth="1" />
      <rect x="36" y="52" width="88" height="6" rx="3" stroke="white" strokeWidth="1" />
      <rect x="36" y="64" width="54" height="5" rx="2.5" stroke="white" strokeWidth="0.75" />

      <rect x="160" y="12" width="120" height="72" rx="12" stroke="white" strokeWidth="1.5" />
      <circle cx="180" cy="32" r="5" stroke="white" strokeWidth="1.5" />
      <rect x="194" y="28" width="48" height="7" rx="3.5" stroke="white" strokeWidth="1" />
      <rect x="176" y="52" width="88" height="6" rx="3" stroke="white" strokeWidth="1" />
      <rect x="176" y="64" width="40" height="5" rx="2.5" stroke="white" strokeWidth="0.75" />

      <rect x="20" y="104" width="260" height="14" rx="7" stroke="white" strokeWidth="1.5" />
      <rect x="20" y="104" width="96" height="14" rx="7" stroke="white" strokeWidth="1" />
      <rect x="116" y="104" width="70" height="14" rx="7" stroke="white" strokeWidth="1" />

      <rect x="20" y="140" width="260" height="52" rx="12" stroke="white" strokeWidth="1.5" />
      <rect x="36" y="158" width="74" height="7" rx="3.5" stroke="white" strokeWidth="1" />
      <rect x="36" y="172" width="130" height="5" rx="2.5" stroke="white" strokeWidth="0.75" />
      <rect x="206" y="156" width="58" height="20" rx="10" stroke="white" strokeWidth="1.5" />

      <rect x="20" y="208" width="260" height="40" rx="10" stroke="white" strokeWidth="1.5" />
      <circle cx="44" cy="228" r="8" stroke="white" strokeWidth="1" />
      <rect x="62" y="222" width="86" height="5" rx="2.5" stroke="white" strokeWidth="1" />
      <rect x="62" y="232" width="52" height="4" rx="2" stroke="white" strokeWidth="0.75" />
      <rect x="212" y="220" width="52" height="16" rx="8" stroke="white" strokeWidth="1" />
    </svg>
  )
}

/**
 * The two panels this screen sits in.
 *
 * Kept as a wrapper rather than folded into the one form left, because the
 * states that shared it are a route away from returning: a recovery form, or a
 * second sign-in method, would want exactly this and would otherwise build a
 * near copy of it, which is how the three that used to live here drifted apart.
 */
function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="auth-screen">
      {/* Desktop only. On a phone the brand panel would push the form below the
          fold, which is a worse trade than not showing it at all. */}
      <aside className="auth-brand">
        <div className="auth-glow auth-glow-one" />
        <div className="auth-glow auth-glow-two" />
        <BoardWireframe />
        <div className="auth-brand-words">
          <span className="asvise-mark auth-brand-mark" role="img" aria-label="Asvise" />
          <h2>
            Know what your family
            <br />
            would fall back on.
          </h2>
          <p>
            See what could leave your family short, what the policies you already hold actually
            cover, and what is worth looking at first.
          </p>
        </div>
      </aside>

      <div className="auth-form-panel">
        <div className="auth-form">
          {/* The mark the brand panel would have carried, for the width where
              there is no brand panel. */}
          <span className="asvise-mark auth-small-mark" role="img" aria-label="Asvise" />
          {children}
        </div>
      </div>
    </div>
  )
}

export function Auth() {
  /**
   * Two states, not two ways in. The reset form is the same screen asking a
   * smaller question, and it goes back rather than forward.
   */
  const [asking, setAsking] = useState(false)
  const [sent, setSent] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supabase) return
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (err) setError(readable(err.message))
  }

  const askForReset = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!supabase) return
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    setBusy(false)
    /**
     * An address with no account is answered exactly like one that has. Signup
     * is closed, so being refused is the common case rather than the odd one,
     * and surfacing it would turn this form into a way to ask, one address at a
     * time and with no password needed to try, which businesses are customers.
     * Anything else is a real failure and is shown.
     */
    if (err && !NO_SUCH_ACCOUNT.test(err.message)) {
      setError(readable(err.message))
      return
    }
    setSent(true)
  }

  const back = () => {
    setAsking(false)
    setSent(false)
    setError(null)
  }

  if (sent) {
    return (
      <Frame>
        <div className="auth-heading">
          <h2>Check your email</h2>
          <p>
            If <strong>{email}</strong> has an account, a link to set a new password is on its way.
            Open it on this device.
          </p>
        </div>
        <button className="primary auth-submit" onClick={back}>
          Back to sign in
        </button>
      </Frame>
    )
  }

  if (asking) {
    return (
      <Frame>
        <div className="auth-heading">
          <h2>Reset your password</h2>
          <p>We will email you a link that lets you set a new one.</p>
        </div>
        <form className="auth-fields" onSubmit={askForReset}>
          <div className="auth-field">
            <label htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              type="email"
              required
              value={email}
              placeholder="you@example.com"
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && <p className="error auth-error">{error}</p>}
          <button type="submit" className="primary auth-submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <p className="auth-alternative">
          <button className="link" onClick={back}>
            Back to sign in
          </button>
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <div className="auth-heading">
        <h2>Sign in</h2>
        <p>Your family's record is private to your account. Accounts are set up for you.</p>
      </div>
      <form className="auth-fields" onSubmit={signIn}>
        <div className="auth-field">
          <label htmlFor="auth-email">Email</label>
          <input
            id="auth-email"
            type="email"
            required
            value={email}
            placeholder="you@example.com"
            autoComplete="email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="auth-field">
          <label htmlFor="auth-password">Password</label>
          <input
            id="auth-password"
            type="password"
            required
            value={password}
            placeholder="Password"
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && <p className="error auth-error">{error}</p>}
        <button type="submit" className="primary auth-submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <p className="auth-alternative">
        <button
          className="link"
          onClick={() => {
            setAsking(true)
            setError(null)
          }}
        >
          Forgot your password?
        </button>
      </p>
    </Frame>
  )
}
